import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { RovoSettingsModalSource } from '@atlassian/rovo-settings-modal/common/utils/rovo-settings-config';
import { RovoSettingsModal } from '@atlassian/rovo-settings-modal/ui/rovo-settings-modal';

// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import { type ProductKeys3P } from '../../../../constants/3p-product-configs';
import {
	NounKeys,
	type NounKeys as NounKeysType,
	nouns,
	NOUNS_ORDER,
} from '../../../../constants/nouns';
import {
	getCategoryForProduct,
	type ProductCategoryConfig,
} from '../../../../constants/product-categories';
import {
	productsSupportingEmptyQuery,
	type PrimaryProductKey,
	ProductKeys,
	ProductKeys as ProductKeysObject,
	useProductConfigs,
} from '../../../../constants/products';
import {
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../../constants/schemas/3p-config';
import { SPOTLIGHT_ONBOARDING_STEP1_TARGET } from '../../../../constants/spotlight';
import { type TriggerComponentProps } from '../../../../types';
import { getShouldShowNouns } from '../../../../utils/app-filter-options';
import { isProductConnected, isProductSmartlink } from '../../../../utils/connectors';
import { OAuthSource } from '../../../../utils/events/oauth-events';
import { onAppFilterOptionClicked } from '../../../../utils/events/product-option-clicked';
import {
	onAppFilterOptionHovered,
	onAppFilterMenuButtonHovered,
	onAppFilterHideButtonHovered,
	onAppFilterShowMoreClicked,
	onAppFilterRendered,
} from '../../../../utils/events/right-panel-app-filter-events';
import {
	getUnifiedSearchRightRailEnhancementsExpValue,
	serveSearchSettingsModalExperiment,
	triggerExposureForSearchSettingsModalExperiment,
} from '../../../../utils/experiments';
import { getProductOrder, getProductsOrderByPrimaryProduct } from '../../../../utils/product';
import { getWorkspaceNameFromProduct } from '../../../../utils/search-result-utils';
import { useDebouncedCallback } from '../../../../utils/use-debounced-callback';
import { AccordionItem } from '../../../accordion';
import { CustomSpotlightTarget } from '../../../custom-spotlight-target';
import {
	OAuthWarningWrapper,
	type UseIsUserOAuthed,
	type UseOAuthHandlers,
} from '../../../oauth-warning-wrapper';
import { AccordionFilterTrigger } from '../accordion-filter-trigger';
import FilterOption from '../filter-option';
import { messages } from '../messages';
import ShowMoreAppsPopup from '../show-more-apps-popup';
import { messages as showMoreMessages } from '../show-more-apps-popup/messages';

import {
	getNestedProducts,
	getProductOrderWithDynamicSections,
	Sections,
} from './dynamic-sections';
import {
	applyFanoutToHiddenApps,
	buildSingleSelectVisibleAndHidden,
	categorizeAppsByAuthStatus,
	createFanoutOptions,
	getSectionVisibilityLimits,
	sortByResultCount,
	type FilterOption as UtilsFilterOption,
} from './utils';

const TRIGGER_HEIGHT_OFFSET = 24;

type SidebarStoredState = {
	filters: Record<string, { hidden: boolean }>;
};

type FilterOption = UtilsFilterOption & {
	productCategory?: ProductCategoryConfig;
};

interface FilterDataOption {
	queryParamValue: string;
	isSelected: boolean;
}

export interface AppFilterProps {
	availableProducts: ProductKeys[];
	availableNouns: NounKeysType[];
	selectedProduct?: ProductKeys;
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	onSelectProduct: (
		productKey: ProductKeys | undefined,
		datasource?: ThirdPartyConfigsWithAPIResponse | string,
	) => void;
	productResultCountMap: { [key: string]: number };
	isNounsAvailable: boolean;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	sidebarStoredState?: SidebarStoredState;
	onFilterHide: (productKey: ProductKeys) => void;
	onFilterShow: (productKey: ProductKeys) => void;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	searchQuery: string;
	primaryProduct?: PrimaryProductKey;
	cloudId?: string;
	isInitialState: boolean;
	recommended3pApps: string[];
	onVisibleProductsToAuthShown: (options: ProductKeys[], recommended3pApps: string[]) => void;
	personalisedProductSections?: Map<Sections, ProductKeys[]>;
	setPersonalisedProductSections?: (sections: Map<Sections, ProductKeys[]>) => void;
	productsWithTimeout?: ProductKeys[];
	resultCountsLoading?: boolean;
	setAtlasNoun?: (noun?: NounKeysType) => void;
	setWebPageType?: () => void;
	filterData?: {
		options: FilterDataOption[];
	};
}

const dividerStyles = xcss({
	width: '100%',
	borderBlockEnd: 'none',
	borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
	borderInline: 'none',
	marginTop: 'space.075',
	marginBottom: 'space.100',
});

export const Divider: () => React.JSX.Element = (): React.JSX.Element => (
	<Box xcss={dividerStyles} />
);

const sectionHeaderStyles = xcss({
	paddingTop: 'space.025',
	paddingBottom: 'space.100',
	paddingInline: 'space.050',
	font: token('font.body'),
	fontWeight: token('font.weight.bold'),
});

const unconnectedAppsHeaderStyleOverrides = xcss({
	paddingTop: 'space.025',
	paddingInline: 'space.050',
	font: token('font.body'),
	fontWeight: token('font.weight.bold'),
});

const SectionHeader = ({
	children,
	styleOverrides,
}: {
	children: React.ReactNode;
	styleOverrides?: ReturnType<typeof xcss>;
}): React.JSX.Element => <Box xcss={styleOverrides ?? sectionHeaderStyles}>{children}</Box>;

/**
 * Renders a FilterOption wrapped in OAuthWarningWrapper with consistent props.
 * Encapsulates the common pattern used throughout the app filters.
 */
const FilterOptionWithOAuth = ({
	option,
	index,
	useOAuthHandlers,
	useIsUserOAuthed,
	thirdPartyConfigs,
	selectedDatasource,
	isInShowMore = false,
}: {
	option: FilterOption;
	index?: number;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	isInShowMore?: boolean;
}): React.JSX.Element => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const isSmartlink = isProductSmartlink({
		productKey: option.value,
		thirdPartyConfigs,
	});

	const isConnected = isProductConnected(option.value, thirdPartyConfigs);

	const debouncedOnHover = useDebouncedCallback(
		useCallback(() => {
			fireAnalyticsEvent(
				onAppFilterOptionHovered({
					commonAttributes,
					nonPrivacySafeAttributes,
					productKey: option.value as ProductKeys,
					isConnected,
					index,
					isInShowMore,
				}),
			);
		}, [
			fireAnalyticsEvent,
			commonAttributes,
			nonPrivacySafeAttributes,
			option.value,
			isConnected,
			index,
			isInShowMore,
		]),
		500,
	);

	const debouncedOnHoverMenuButton = useDebouncedCallback(
		useCallback(() => {
			fireAnalyticsEvent(
				onAppFilterMenuButtonHovered({
					commonAttributes,
					nonPrivacySafeAttributes,
					productKey: option.value as ProductKeys,
					isConnected,
				}),
			);
		}, [fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes, option.value, isConnected]),
		500,
	);

	const debouncedOnHoverMenuItem = useDebouncedCallback(
		useCallback(() => {
			fireAnalyticsEvent(
				onAppFilterHideButtonHovered({
					commonAttributes,
					nonPrivacySafeAttributes,
					productKey: option.value as ProductKeys,
					isConnected,
				}),
			);
		}, [fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes, option.value, isConnected]),
		500,
	);

	return (
		<OAuthWarningWrapper
			datasourceId={option.datasourceId ?? selectedDatasource?.datasourceId}
			product={option.value as ProductKeys}
			trigger={({ isDisabled, oAuthWarning, triggerProps }: TriggerComponentProps) => (
				<FilterOption
					oAuthWarning={oAuthWarning}
					triggerProps={triggerProps}
					key={option.compositeKey ?? option.value}
					isDisabled={isDisabled}
					option={option}
					onClick={() => {
						fireAnalyticsEvent(
							onAppFilterOptionClicked({
								commonAttributes,
								nonPrivacySafeAttributes,
								productKey: option.value as ProductKeys,
								isConnected,
								index,
								isInShowMore,
							}),
						);
						option.onSelect();
					}}
					onHover={debouncedOnHover}
					onHoverMenuButton={debouncedOnHoverMenuButton}
					onHoverMenuItem={debouncedOnHoverMenuItem}
					onClickMenuItem={(_, action: string) => {
						if (action === 'hide') {
							// onFilterHide in right-panel fires onAppFilterShowHideClicked — no need to fire here too
							option.hideFilter();
						}
					}}
					ariaRole="listitem"
				/>
			)}
			index={index}
			useOAuthHandlers={useOAuthHandlers}
			useIsUserOAuthed={useIsUserOAuthed}
			isSmartlink={isSmartlink}
			oAuthSource={OAuthSource.searchPageFilter}
			thirdPartyConfigs={thirdPartyConfigs}
			option={
				getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select' ? option : undefined
			}
			skipFlagsProvider={getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'}
		/>
	);
};

/**
 * Renders an accordion item for nested apps (e.g., Jira, Confluence, JSM with cloud/DC variants).
 */
const AccordionFilterItem = ({
	option,
	primaryProduct,
	anyConnectedConfluenceDC,
	anyConnectedJiraDC,
	anyConnectedJsmDC,
	resultCountsLoading,
	useOAuthHandlers,
	useIsUserOAuthed,
	thirdPartyConfigs,
	selectedDatasource,
}: {
	option: FilterOption;
	primaryProduct?: PrimaryProductKey;
	anyConnectedConfluenceDC: boolean;
	anyConnectedJiraDC: boolean;
	anyConnectedJsmDC: boolean;
	resultCountsLoading: boolean;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
}): React.JSX.Element | null => {
	if (!option.nestedApps || option.nestedApps.length <= 1 || !option.productCategory) {
		return null;
	}

	// Check if accordion children are in the visible slice (for single-select accordion collapse behavior)
	// For single-select: use __hasVisibleChildren metadata set by buildSingleSelectVisibleAndHidden
	// For control/multi-select: fallback to checking if nestedApps exists
	const hasVisibleChildren =
		getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
			? (option.__hasVisibleChildren ?? false)
			: option.nestedApps && option.nestedApps.length > 1;

	const FilterTriggerIcon = option.productCategory.Icon;
	const hasSelection = option.nestedApps?.find(
		(nestedApp: FilterOption) => nestedApp?.isSelected,
	)?.isSelected;

	const totalCount = option.nestedApps?.reduce(
		(prev: number | undefined, curr: FilterOption) =>
			curr.count === undefined ? prev : (prev ?? 0) + curr.count,
		undefined,
	);
	const accordionCategory = option.productCategory.productCategory;

	let defaultIsExpanded = false;
	let isInitiallyExpanded = false;

	if (fg('rovo-search-right-rail-personalisation')) {
		const isPrimaryProductToExpand = primaryProduct === option.value;
		if (getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select') {
			// In single-select: defaultIsExpanded is driven by hasVisibleChildren so viewport resize
			// correctly triggers collapse/expand in AccordionItem (prev→now transition).
			// isInitiallyExpanded force-opens the accordion on mount for the primary product,
			// even when hasVisibleChildren=false at a small initial viewport.
			defaultIsExpanded = (!!hasSelection || isPrimaryProductToExpand) && hasVisibleChildren;
			isInitiallyExpanded = isPrimaryProductToExpand || (!!hasSelection && hasVisibleChildren);
		} else {
			// Control: preserve original behavior — isPrimaryProductToExpand keeps accordion open
			// regardless of hasVisibleChildren (viewport-agnostic).
			defaultIsExpanded =
				(hasSelection || isPrimaryProductToExpand) &&
				(hasVisibleChildren || isPrimaryProductToExpand);
		}
	} else {
		const expandConfluenceApps =
			accordionCategory === ProductKeys.Confluence && anyConnectedConfluenceDC;
		const expandJiraApps = accordionCategory === ProductKeys.Jira && anyConnectedJiraDC;
		const expandJSMApps =
			accordionCategory === ProductKeys.JiraServiceManagement && anyConnectedJsmDC;
		// Only expand if children are visible to show
		defaultIsExpanded =
			(hasSelection || expandConfluenceApps || expandJiraApps || expandJSMApps) &&
			hasVisibleChildren;
	}

	return (
		<Box role="listitem">
			<AccordionItem
				triggerComponent={
					<AccordionFilterTrigger
						// icons should be xxsmall (16px) in single-select variant
						icon={
							<FilterTriggerIcon
								size={
									getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
										? 'xxsmall'
										: 'small'
								}
							/>
						}
						label={option.productCategory.displayName}
						count={totalCount}
						countLoading={resultCountsLoading}
					/>
				}
				groupId={option.value || 'unknown'}
				defaultIsExpanded={defaultIsExpanded}
				isInitiallyExpanded={isInitiallyExpanded}
				hasSelection={hasSelection}
				onClick={
					// For single-select: don't auto-select on expand to avoid re-render/remount
					// For control/multi-select: keep original auto-select behavior
					getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
						? undefined
						: (openState: boolean) => {
								if (!openState) {
									return;
								}
								if (
									option.nestedApps &&
									option.nestedApps.find((nested: FilterOption) => nested.isSelected)
								) {
									return;
								}
								option.nestedApps?.[0]?.onSelect();
							}
				}
			>
				{option.nestedApps && option.nestedApps.length > 1
					? option.nestedApps.map((nestedOption: FilterOption) => (
							<FilterOptionWithOAuth
								key={nestedOption.compositeKey ?? nestedOption.value}
								option={nestedOption}
								useOAuthHandlers={useOAuthHandlers}
								useIsUserOAuthed={useIsUserOAuthed}
								thirdPartyConfigs={thirdPartyConfigs}
								selectedDatasource={selectedDatasource}
							/>
						))
					: null}
			</AccordionItem>
		</Box>
	);
};

/**
 * Renders a product option with fanout support.
 * Handles both fanout products (multiple datasources) and regular products.
 */
const ProductOption = ({
	option,
	index,
	thirdPartyConfigs,
	selectedProduct,
	selectedDatasource,
	onSelectProduct,
	useOAuthHandlers,
	useIsUserOAuthed,
	allApps,
}: {
	option: FilterOption;
	index: number;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	selectedProduct?: ProductKeys;
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	onSelectProduct: (
		productKey: ProductKeys | undefined,
		datasource?: ThirdPartyConfigsWithAPIResponse | string,
	) => void;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	allApps?: { hiddenApps: FilterOption[] };
}): React.JSX.Element => {
	const hasFanout = thirdPartyConfigs[option.value as ProductKeys3P]?.fanOutConnectorSources;

	if (hasFanout) {
		let optionsToRender = createFanoutOptions(
			option,
			thirdPartyConfigs,
			selectedProduct,
			selectedDatasource,
			onSelectProduct,
		);

		if (
			option.value === ProductKeysObject.AtlassianWebCrawler &&
			option.value === selectedProduct &&
			selectedDatasource &&
			allApps?.hiddenApps &&
			allApps.hiddenApps.length > 0
		) {
			optionsToRender = optionsToRender.filter((renderOption) => renderOption.isSelected);
		}

		return (
			<>
				{optionsToRender.map((renderOption) => (
					<FilterOptionWithOAuth
						key={renderOption.compositeKey ?? renderOption.value}
						option={renderOption}
						index={index}
						useOAuthHandlers={useOAuthHandlers}
						useIsUserOAuthed={useIsUserOAuthed}
						thirdPartyConfigs={thirdPartyConfigs}
						selectedDatasource={selectedDatasource}
					/>
				))}
			</>
		);
	}

	return (
		<FilterOptionWithOAuth
			option={option}
			index={index}
			useOAuthHandlers={useOAuthHandlers}
			useIsUserOAuthed={useIsUserOAuthed}
			thirdPartyConfigs={thirdPartyConfigs}
			selectedDatasource={selectedDatasource}
		/>
	);
};

/**
 * Renders a list of product options, handling both accordion items and regular products.
 */
const ProductOptions = ({
	options,
	thirdPartyConfigs,
	selectedProduct,
	selectedDatasource,
	onSelectProduct,
	useOAuthHandlers,
	useIsUserOAuthed,
	allApps,
	primaryProduct,
	anyConnectedConfluenceDC,
	anyConnectedJiraDC,
	anyConnectedJsmDC,
	resultCountsLoading,
}: {
	options: FilterOption[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	selectedProduct?: ProductKeys;
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	onSelectProduct: (
		productKey: ProductKeys | undefined,
		datasource?: ThirdPartyConfigsWithAPIResponse | string,
	) => void;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	allApps?: { hiddenApps: FilterOption[] };
	primaryProduct?: PrimaryProductKey;
	anyConnectedConfluenceDC?: boolean;
	anyConnectedJiraDC?: boolean;
	anyConnectedJsmDC?: boolean;
	resultCountsLoading?: boolean;
}): React.JSX.Element => (
	<>
		{options.map((option, index) => {
			if (option.nestedApps && option.nestedApps.length > 1 && option.productCategory) {
				return (
					<AccordionFilterItem
						key={option.productCategory.productCategory}
						option={option}
						primaryProduct={primaryProduct}
						anyConnectedConfluenceDC={anyConnectedConfluenceDC ?? false}
						anyConnectedJiraDC={anyConnectedJiraDC ?? false}
						anyConnectedJsmDC={anyConnectedJsmDC ?? false}
						resultCountsLoading={resultCountsLoading ?? false}
						useOAuthHandlers={useOAuthHandlers}
						useIsUserOAuthed={useIsUserOAuthed}
						thirdPartyConfigs={thirdPartyConfigs}
						selectedDatasource={selectedDatasource}
					/>
				);
			}

			return (
				<ProductOption
					key={option.compositeKey ?? option.value}
					option={option}
					index={index}
					thirdPartyConfigs={thirdPartyConfigs}
					selectedProduct={selectedProduct}
					selectedDatasource={selectedDatasource}
					onSelectProduct={onSelectProduct}
					useOAuthHandlers={useOAuthHandlers}
					useIsUserOAuthed={useIsUserOAuthed}
					allApps={allApps}
				/>
			);
		})}
	</>
);

/**
 * Trigger component for RovoSettingsModal that renders a FilterOption
 * styled as the "Show all apps" button.
 */
const RovoSettingsModalTrigger = ({
	open,
}: {
	open: (options?: { defaultTab?: string }) => void;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	return (
		<FilterOption
			oAuthWarning={null}
			isDisabled={false}
			isShowMore={true}
			option={{
				label: formatMessage(
					getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
						? showMoreMessages.showAllAppsLabel
						: showMoreMessages.showMoreLabel,
				),
				isSelected: false,
				value: undefined,
			}}
			onClick={() => {
				triggerExposureForSearchSettingsModalExperiment();
				open({ defaultTab: 'discovery' });
				fireAnalyticsEvent(
					onAppFilterShowMoreClicked({
						commonAttributes,
						nonPrivacySafeAttributes,
						isConnected: true,
						section: 'connectApps',
					}),
				);
			}}
		/>
	);
};

/**
 * Thin wrapper that renders either the RovoSettingsModal (when the connector
 * console experiment is enabled) or the ShowMoreAppsPopup with consistent props.
 * Exported for testing purposes.
 */
export const ShowMorePopupWrapper = ({
	options,
	useOAuthHandlers,
	useIsUserOAuthed,
	thirdPartyConfigs,
	isConnected = true,
	cloudId,
	primaryProduct,
}: {
	options: FilterOption[];
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	isConnected?: boolean;
	cloudId?: string;
	primaryProduct?: PrimaryProductKey;
}): React.JSX.Element => {
	if (
		!isConnected &&
		cloudId &&
		primaryProduct &&
		serveSearchSettingsModalExperiment({ fireExperimentExposure: false })
	) {
		return (
			<RovoSettingsModal
				product={primaryProduct}
				cloudId={cloudId}
				source={RovoSettingsModalSource.SEARCH}
			>
				{(props) => <RovoSettingsModalTrigger {...props} />}
			</RovoSettingsModal>
		);
	}

	// Fire search experiment exposure for the control cohort at click-time, only
	// when this caller could have rendered the treatment branch above.
	const isEligibleForSearchSettingsModalExperiment = !isConnected && !!cloudId && !!primaryProduct;
	return (
		<ShowMoreAppsPopup
			options={options}
			useIsUserOAuthed={useIsUserOAuthed}
			useOAuthHandlers={useOAuthHandlers}
			thirdPartyConfigs={thirdPartyConfigs}
			oAuthSource={OAuthSource.searchPageFilter}
			shouldFitContainer
			offset={[0, -TRIGGER_HEIGHT_OFFSET]}
			isConnected={isConnected}
			onTriggerClick={
				isEligibleForSearchSettingsModalExperiment
					? triggerExposureForSearchSettingsModalExperiment
					: undefined
			}
		/>
	);
};

// Single-select variant: keep gray section container background
const connectedAppsContainerStylesSingleSelect = xcss({
	background: token('elevation.surface.sunken'),
	borderRadius: token('radius.medium'),
	padding: 'space.150',
	gap: 'space.025',
	display: 'flex',
	flexDirection: 'column',
	marginBottom: 'space.150',
});

// Single-select variant: keep gray section container background
const unconnectedAppsContainerStylesSingleSelect = xcss({
	background: token('elevation.surface.sunken'),
	borderRadius: token('radius.medium'),
	padding: 'space.150',
	gap: 'space.025',
	display: 'flex',
	flexDirection: 'column',
});

const unconnectedAppsDescriptionStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.050',
});

export const getResultCountForAll = (
	productResultCountMap: { [key: string]: number },
	searchQuery: string,
	selectedProduct?: ProductKeys,
): number | undefined => {
	// Only some products allow empty queries so we should only return the count for that product
	if (
		searchQuery === '' &&
		selectedProduct &&
		productsSupportingEmptyQuery.includes(selectedProduct)
	) {
		return undefined;
	}

	return (
		Object.keys(productResultCountMap).reduce(
			(count, key) => (count += productResultCountMap[key]),
			0,
		) || undefined
	);
};

// Module-level constant to avoid repeated Set creation in render loops
const NOUN_KEYS_SET = new Set<string>(Object.values(NounKeys));

const MAX_VISIBLE_APP_FILTERS = 15;
// Ceiling for showFilter - allows user to reveal more apps from Show More popup when viewport allows.
// Visibility limits are computed dynamically via getSectionVisibilityLimits for single-select.
const MAX_VISIBLE_APP_FILTERS_SINGLE_SELECT_CEILING = 20;

const getMaxVisibleAppFilters = () => {
	// For single-select, visibility uses getSectionVisibilityLimits (viewport-based).
	// This is only used for showFilter ceiling when user reveals an app from Show More.
	if (getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select') {
		return MAX_VISIBLE_APP_FILTERS_SINGLE_SELECT_CEILING;
	}

	return MAX_VISIBLE_APP_FILTERS;
};

/**
 * Checks if any datasource for a product is connected (doesn't need OAuth)
 * This is a utility function that avoids using hooks in loops
 *
 * NOTE: First-party Atlassian products (like confluence-cloud, jira-cloud) are NOT in thirdPartyConfigs
 * and are considered automatically connected since they don't require OAuth.
 *
 * For third-party products, userNeedsOAuth indicates whether user needs to authenticate:
 * - userNeedsOAuth: true → NOT connected (needs OAuth)
 * - userNeedsOAuth: false → IS connected (OAuth completed)
 */
const hasAnyAuthedDatasource = (
	productKey: ProductKeys,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): boolean => {
	const productConfig = thirdPartyConfigs[productKey as ProductKeys3P];
	return productConfig?.datasources?.some((ds) => !ds?.userNeedsOAuth) ?? false;
};

export const AppFilters = ({
	onSelectProduct,
	recommended3pApps,
	availableProducts,
	availableNouns,
	selectedProduct,
	selectedDatasource,
	isNounsAvailable,
	useOAuthHandlers,
	useIsUserOAuthed,
	productResultCountMap = {},
	sidebarStoredState,
	onFilterHide,
	onFilterShow,
	thirdPartyConfigs,
	searchQuery,
	primaryProduct,
	cloudId,
	isInitialState,
	onVisibleProductsToAuthShown,
	personalisedProductSections,
	setPersonalisedProductSections,
	productsWithTimeout,
	resultCountsLoading,
	setAtlasNoun,
	setWebPageType,
	filterData,
}: AppFilterProps): React.JSX.Element => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const { formatMessage } = useIntl();
	const products = useProductConfigs();

	const [viewportHeight, setViewportHeight] = useState(() =>
		typeof window !== 'undefined' ? window.innerHeight : 600,
	);
	// Only track viewport for single-select variant (used by getSectionVisibilityLimits)
	useEffect(() => {
		if (
			typeof window !== 'undefined' &&
			getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
		) {
			return bind(window, {
				type: 'resize',
				listener: () => setViewportHeight(window.innerHeight),
			});
		}
	}, []);

	const visibleFiltersCount = useMemo(() => {
		return Object.values(sidebarStoredState?.filters ?? {}).filter((filter) => {
			return filter && typeof filter === 'object' && 'hidden' in filter && !filter.hidden;
		}).length;
	}, [sidebarStoredState?.filters]);

	const isProductHiddenByUser = useMemo(
		() =>
			(productKey: ProductKeys): boolean => {
				return !!sidebarStoredState?.filters?.[productKey]?.hidden;
			},
		[sidebarStoredState?.filters],
	);

	const shouldShowNouns = useMemo(
		() => getShouldShowNouns(isNounsAvailable, availableNouns, availableProducts),
		[isNounsAvailable, availableNouns, availableProducts],
	);

	const productFilters = useMemo(() => {
		const availableProductsWithoutAtlasAndCustomWebsitesIfNounsAvailable = shouldShowNouns
			? availableProducts.filter((key) => {
					// For single-select: always filter out both Atlas and AtlassianWebCrawler when shouldShowNouns.
					// Nouns (including web pages) are created as separate noun options below, so the product
					// entry must be removed to avoid duplicates (even when fanOutConnectorSources is truthy).
					if (getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select') {
						return key !== ProductKeysObject.Atlas && key !== ProductKeysObject.AtlassianWebCrawler;
					}
					// For other cohorts: keep AtlassianWebCrawler if it has fanout (original behavior)
					return thirdPartyConfigs[ProductKeys.AtlassianWebCrawler]?.fanOutConnectorSources
						? key !== ProductKeysObject.Atlas
						: key !== ProductKeysObject.Atlas && key !== ProductKeysObject.AtlassianWebCrawler;
				})
			: availableProducts;

		const createProductOption = (productKey: ProductKeys, section?: Sections): FilterOption => {
			const workspaceName = getWorkspaceNameFromProduct(
				productKey as ProductKeys,
				thirdPartyConfigs,
			);
			const isSelected = productKey === selectedProduct && !selectedDatasource;
			// workspaceName is available for Forge connectors and products with displayWorkspaceNameAsProductName
			// Otherwise fall back to displayName from products map
			const label = workspaceName || products[productKey]?.displayName || productKey;
			const count = productResultCountMap[productKey];
			const isPrimaryProduct = productKey === primaryProduct;

			return {
				label,
				count,
				value: productKey as ProductKeys,
				isSelected,
				isPrimaryProduct,
				productCategory: getCategoryForProduct(productKey as ProductKeys),
				onSelect: () => onSelectProduct(productKey),
				hideFilter: () => {
					onFilterHide(productKey as ProductKeys);
					onSelectProduct(undefined);
				},
				showFilter: () => {
					if (visibleFiltersCount >= getMaxVisibleAppFilters()) {
						return;
					}
					return onFilterShow(productKey as ProductKeys);
				},
				resultCountLimit: productKey === ProductKeysObject.Trello ? 30 : undefined,
				section,
				countLoading: resultCountsLoading,
				timeout: productsWithTimeout?.includes(productKey),
			};
		};

		const productsOrder = getProductsOrderByPrimaryProduct(primaryProduct);
		const availableProductsInDefaultOrder =
			availableProductsWithoutAtlasAndCustomWebsitesIfNounsAvailable
				.filter((productKey) => {
					// Forge connectors are dynamically added and won't be in the static products map
					if (isForgeConnectorProductKey(productKey) && fg('rovo-search-dynamic-forge-config')) {
						return true;
					}
					// For hardcoded products, check if they exist in the products map
					return !!products[productKey];
				})
				.sort((a, b) => getProductOrder(a as ProductKeys, b as ProductKeys, productsOrder));

		let productOptions: FilterOption[];
		if (fg('rovo-search-right-rail-personalisation')) {
			// Ignore counts if:
			//  - we're in the initial state (to show the default order)
			//  - when counts are missing- we want to fall back to the default order and show all apps
			const ignoreCounts = isInitialState || Object.keys(productResultCountMap).length === 0;

			let productOrderWithDynamicSections: Map<Sections, ProductKeys[]> = new Map();

			if (!personalisedProductSections) {
				productOrderWithDynamicSections = getProductOrderWithDynamicSections({
					primaryProduct,
					availableProducts: availableProductsInDefaultOrder,
					productResultCountMap,
					thirdPartyConfigs,
					isProductHiddenByUser,
					ignoreCounts,
					recommended3pApps,
				});

				// Store the order of the personalised sections for future use
				// but only after a successful search has been made
				if (!isInitialState) {
					setPersonalisedProductSections?.(productOrderWithDynamicSections);
				}
			} else {
				productOrderWithDynamicSections = personalisedProductSections;
			}

			// Convert to options
			productOptions = Array.from(productOrderWithDynamicSections.entries()).flatMap(
				([section, products]) =>
					products.map((productKey) => createProductOption(productKey, section)),
			);
		} else {
			productOptions = availableProductsInDefaultOrder.map((productKey) =>
				createProductOption(productKey),
			);
		}

		// For single-select cohort: build noun filter options inline so they appear in the
		// connected apps section and respect viewport-based visibility limits like any other app.
		let nounOptions: FilterOption[] = [];
		if (shouldShowNouns && getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select') {
			const isWebCrawlerAvailable = availableProducts.includes(
				ProductKeysObject.AtlassianWebCrawler,
			);
			const NOUNS_ORDER_WITH_WEBCRAWLER = isWebCrawlerAvailable
				? NOUNS_ORDER
				: NOUNS_ORDER.filter((noun) => noun !== NounKeys.AtlassianWebCrawler);

			let nounsToDisplay: NounKeysType[];

			if (!isNounsAvailable) {
				nounsToDisplay = NOUNS_ORDER_WITH_WEBCRAWLER.filter(
					(noun) =>
						availableNouns.includes(noun) ||
						(isWebCrawlerAvailable && noun === NounKeys.AtlassianWebCrawler),
				);
			} else {
				nounsToDisplay = NOUNS_ORDER_WITH_WEBCRAWLER;
			}

			const isAtlasSelected = selectedProduct === ProductKeysObject.Atlas;

			nounOptions = nounsToDisplay.map((noun) => ({
				label: formatMessage(nouns[noun].displayNamePlural),
				value: noun as unknown as ProductKeys,
				isSelected:
					noun === NounKeys.AtlassianWebCrawler
						? selectedProduct === ProductKeysObject.AtlassianWebCrawler
						: isAtlasSelected &&
							(filterData?.options.find((option) => option.queryParamValue === noun)?.isSelected ??
								false),
				count: productResultCountMap[noun],
				//web crawler is rendered as a noun, but queried as a product
				onSelect:
					noun === NounKeys.AtlassianWebCrawler
						? () => setWebPageType?.()
						: () => setAtlasNoun?.(noun),
				hideFilter: () => {},
				showFilter: () => {},
				countLoading: resultCountsLoading,
			}));
		}

		return [
			// All filter
			{
				count: getResultCountForAll(productResultCountMap, searchQuery, selectedProduct),
				label: formatMessage(messages.allFilterLabel),
				value: undefined,
				isSelected: !selectedProduct,
				productCategory: undefined,
				onSelect: () => onSelectProduct(undefined, undefined),
				hideFilter: () => {},
				showFilter: () => {},
				countLoading: resultCountsLoading,
			},
			...productOptions,
			...nounOptions,
		];
	}, [
		availableProducts,
		formatMessage,
		onSelectProduct,
		selectedProduct,
		selectedDatasource,
		productResultCountMap,
		onFilterHide,
		onFilterShow,
		thirdPartyConfigs,
		products,
		visibleFiltersCount,
		searchQuery,
		primaryProduct,
		isInitialState,
		isProductHiddenByUser,
		recommended3pApps,
		personalisedProductSections,
		setPersonalisedProductSections,
		productsWithTimeout,
		resultCountsLoading,
		shouldShowNouns,
		availableNouns,
		isNounsAvailable,
		filterData,
		setAtlasNoun,
		setWebPageType,
	]);

	const allApps = useMemo(() => {
		let visibleApps: FilterOption[];
		let hiddenApps: FilterOption[];

		if (getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select') {
			const { visibleApps: visibleAppsFull, hiddenApps: hiddenAppsOther } = productFilters.reduce(
				(acc, option) => {
					const visibility = getAppVisibility(
						option,
						sidebarStoredState,
						acc.visibleApps,
						onFilterHide,
						selectedProduct,
						selectedDatasource,
						thirdPartyConfigs,
					);
					if (visibility === 'visible') {
						acc.visibleApps.push(option);
					} else {
						acc.hiddenApps.push(option);
					}
					return acc;
				},
				{ visibleApps: [] as FilterOption[], hiddenApps: [] as FilterOption[] },
			);

			// When shouldShowNouns is true, keep nouns in the visible apps list so they appear
			// inline in the connected apps section and respect viewport-based visibility limits.
			// When shouldShowNouns is false, exclude nouns as before.
			const appsForCategorization = shouldShowNouns
				? visibleAppsFull
				: visibleAppsFull.filter((app) => app.value === undefined || !NOUN_KEYS_SET.has(app.value));
			const { connectedApps: connectedAppsUnsorted, unconnectedApps } = categorizeAppsByAuthStatus({
				visibleApps: appsForCategorization,
				thirdPartyConfigs,
				hiddenApps: [],
			});

			// Re-sort connectedApps by result count (descending) to ensure proper ranking
			// This is necessary because categorizeAppsByAuthStatus preserves the original order,
			// which may have apps from different sections (e.g., VISIBLE_PRODUCTS_TO_AUTH) mixed in
			const connectedApps = sortByResultCount([...connectedAppsUnsorted]);

			const { connectedMax, unconnectedMax } = getSectionVisibilityLimits(viewportHeight, {
				connectedCount: connectedApps.length,
				unconnectedCount: unconnectedApps.length,
			});

			({ visibleApps, hiddenApps } = buildSingleSelectVisibleAndHidden({
				visibleAppsFull,
				connectedApps,
				unconnectedApps,
				connectedMax,
				unconnectedMax,
				hiddenAppsOther,
				thirdPartyConfigs,
				sidebarStoredState,
				selectedProduct,
				primaryProduct,
			}));
		} else {
			const reduced = productFilters.reduce(
				(acc, option) => {
					const visibility = getAppVisibility(
						option,
						sidebarStoredState,
						acc.visibleApps,
						onFilterHide,
						selectedProduct,
						selectedDatasource,
						thirdPartyConfigs,
					);
					if (visibility === 'visible') {
						acc.visibleApps.push(option);
					} else {
						acc.hiddenApps.push(option);
					}
					return acc;
				},
				{ visibleApps: [] as FilterOption[], hiddenApps: [] as FilterOption[] },
			);
			visibleApps = reduced.visibleApps;
			hiddenApps = reduced.hiddenApps;
		}

		// Sort hidden apps by result count (descending) so they appear in the correct order in ShowMoreApps popup
		// On page refresh, getProductOrderWithDynamicSections sorts everything upfront, including hidden items.
		// But when a user immediately hides a product, it transitions from visible→hidden with its original ordering,
		// so we must re-sort here to match the refresh behavior.
		sortByResultCount(hiddenApps);

		// Note: Special handling for selected datasource is now done in the fanout rendering logic
		// to preserve the original ordering position of the product

		const nestedProducts = getNestedProducts();
		const appsToDisplaySet = new Set<string>();

		// For single-select: combine visibleApps and hiddenApps for searching nested children
		// This ensures accordion children are found even when viewport constraints push them to hidden
		// For control/multi-select: only search in visibleApps (original behavior)
		const allAppsForNesting =
			getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
				? [...visibleApps, ...hiddenApps]
				: visibleApps;

		const visibleAppsWithCategories = visibleApps
			.filter((visibleApp) => visibleApp != null)
			.filter((visibleApp) => {
				if (fg('rovo-search-right-rail-personalisation')) {
					// Exclude nested products from the main list since they'll be shown within their category.
					// This also means the nested products (eg. DC) will not be shown in the dropdown- only their parent.
					// For single-select: noun options (e.g. 'atlassian-web-crawler') must NOT be excluded even
					// though they appear in productCategories as nested products — they are standalone inline
					// filter options, not accordion children.
					if (
						NOUN_KEYS_SET.has(visibleApp.value as string) &&
						getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
					) {
						return true;
					}
					return !nestedProducts.has(visibleApp.value as ProductKeys);
				}
				return true;
			})
			.map((visibleApp) => {
				return {
					...visibleApp,
					nestedApps:
						// Populate products within its respective category
						// Single-select: Search in BOTH visible and hidden apps so accordion children are available
						// Control/multi-select: Only search in visible apps (original behavior)
						visibleApp.productCategory?.products.flatMap((productWithinCategory: ProductKeys) => {
							// For single-select: skip noun values when building accordion children.
							// Nouns are standalone inline options, not accordion children.
							if (
								getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select' &&
								NOUN_KEYS_SET.has(productWithinCategory)
							) {
								return [];
							}

							const nestedApp = allAppsForNesting.find(
								(app) => app && app.value === productWithinCategory,
							);
							if (!nestedApp) {
								return [];
							}

							// Apply fanout logic to nested apps to get correct selection state
							const hasFanout =
								thirdPartyConfigs[nestedApp.value as ProductKeys3P]?.fanOutConnectorSources;

							if (hasFanout) {
								// For fanout products, use the fanout options to get correct selection state
								const fanoutOptions = createFanoutOptions(
									nestedApp,
									thirdPartyConfigs,
									selectedProduct,
									selectedDatasource,
									onSelectProduct,
								);

								// Otherwise return the first fanout option (which will have the correct base state)
								return fanoutOptions;
							}

							return [nestedApp];
						}) ?? [],
				};
			})
			.filter((visibleApp) => {
				// The 'All' case should always be displayed
				if (!visibleApp.value) {
					return true;
				}

				// Don't display apps that are already in a nested category
				if (appsToDisplaySet.has(visibleApp.value)) {
					return false;
				}

				visibleApp.nestedApps.forEach(
					(nestedApp: FilterOption) => nestedApp?.value && appsToDisplaySet.add(nestedApp.value),
				);

				return true;
			});

		// Apply fanout logic to hidden apps for show more popup
		const hiddenAppsWithFanout = applyFanoutToHiddenApps(
			hiddenApps,
			thirdPartyConfigs,
			selectedProduct,
			selectedDatasource,
			onSelectProduct,
		);

		// For single-select: filter nouns from visible apps and return pre-categorized results
		// to avoid re-running categorizeAppsByAuthStatus in groupedAppsBySection.
		// For non-single-select: visibleApps are returned as-is (nouns handled by NounFilters component).
		const filteredVisibleApps =
			!shouldShowNouns && getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
				? visibleAppsWithCategories.filter(
						(app) => app.value === undefined || !NOUN_KEYS_SET.has(app.value),
					)
				: visibleAppsWithCategories;

		// Pre-categorize for single-select to avoid double categorization in groupedAppsBySection
		const categorized =
			getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
				? categorizeAppsByAuthStatus({
						visibleApps: filteredVisibleApps,
						thirdPartyConfigs,
						hiddenApps: hiddenAppsWithFanout,
					})
				: undefined;

		return {
			visibleApps: filteredVisibleApps,
			hiddenApps: hiddenAppsWithFanout,
			categorized,
		};
	}, [
		productFilters,
		sidebarStoredState,
		onFilterHide,
		thirdPartyConfigs,
		selectedProduct,
		primaryProduct,
		selectedDatasource,
		onSelectProduct,
		viewportHeight,
		shouldShowNouns,
	]);

	onVisibleProductsToAuthShown(
		allApps.visibleApps
			.filter(
				(o) =>
					o !== undefined &&
					o.value !== undefined &&
					o.section === Sections.VISIBLE_PRODUCTS_TO_AUTH,
			)
			.map((o) => o.value)
			.filter((o) => o !== undefined),
		recommended3pApps,
	);

	// Memoize the OAuth connection states to avoid recalculating on every render
	const oAuthConnectionStates = useMemo(
		() => ({
			anyConnectedConfluenceDC: hasAnyAuthedDatasource(ProductKeys.ConfluenceDC, thirdPartyConfigs),
			anyConnectedJiraDC: hasAnyAuthedDatasource(ProductKeys.JiraDC, thirdPartyConfigs),
			anyConnectedJsmDC: hasAnyAuthedDatasource(ProductKeys.JsmDC, thirdPartyConfigs),
		}),
		[thirdPartyConfigs],
	);

	const { anyConnectedConfluenceDC, anyConnectedJiraDC, anyConnectedJsmDC } = oAuthConnectionStates;

	const groupedAppsBySection = useMemo(() => {
		// For single-select: reuse pre-categorized results from allApps to avoid redundant work
		if (allApps.categorized) {
			return {
				apps: allApps.visibleApps,
				connectedApps: allApps.categorized.connectedApps,
				unconnectedApps: allApps.categorized.unconnectedApps,
				connectedHiddenApps: allApps.categorized.connectedHiddenApps,
				unconnectedHiddenApps: allApps.categorized.unconnectedHiddenApps,
			};
		}

		// For non-single-select: categorize here (original behavior)
		const { connectedApps, unconnectedApps, unconnectedHiddenApps, connectedHiddenApps } =
			categorizeAppsByAuthStatus({
				visibleApps: allApps.visibleApps,
				thirdPartyConfigs,
				hiddenApps: allApps.hiddenApps,
			});

		return {
			apps: allApps.visibleApps,
			connectedApps,
			unconnectedApps,
			connectedHiddenApps,
			unconnectedHiddenApps,
		};
	}, [allApps, thirdPartyConfigs]);

	// Fires exactly once when results first appear (isInitialState flips false).
	// hasRenderedRef prevents re-firing if deps change after the initial render.
	const hasRenderedRef = useRef(false);
	useEffect(() => {
		if (!isInitialState && !hasRenderedRef.current) {
			hasRenderedRef.current = true;
			const numOfFilters = allApps.visibleApps.length;
			const numOfUnconnectedApps = allApps.visibleApps.filter((opt) => {
				if (!opt?.value) return false;
				const productConfig = thirdPartyConfigs[opt.value as ProductKeys3P];
				return productConfig?.datasources?.some((ds) => ds?.userNeedsOAuth) ?? false;
			}).length;
			fireAnalyticsEvent(
				onAppFilterRendered({
					commonAttributes,
					nonPrivacySafeAttributes,
					numOfFilters,
					numOfUnconnectedApps,
					viewportHeight: window.innerHeight,
					viewportWidth: window.innerWidth,
				}),
			);
		}
	}, [
		isInitialState,
		allApps,
		thirdPartyConfigs,
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
	]);

	return (
		<UFOSegment name="search-page-app-filters">
			<CustomSpotlightTarget name={SPOTLIGHT_ONBOARDING_STEP1_TARGET}>
				<Box role="list">
					{getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select' &&
					groupedAppsBySection &&
					groupedAppsBySection.apps.length > 0 ? (
						// New experiment UI with section headers
						[
							// Render connected apps section with "All" option at the top
							...((groupedAppsBySection.connectedApps.length ?? 0) > 0 ||
							allApps.visibleApps.some((app) => app.value === undefined)
								? [
										<Box xcss={connectedAppsContainerStylesSingleSelect} key="connected-section">
											<SectionHeader key="connected-header">
												{formatMessage(messages.connectedAppsHeader)}
											</SectionHeader>
											{/* First, render the "All" option inside the container */}
											{allApps.visibleApps
												.filter((app) => app.value === undefined)
												.map((option, index) => (
													<FilterOptionWithOAuth
														key={option.value ?? 'all'}
														option={option}
														index={index}
														useOAuthHandlers={useOAuthHandlers}
														useIsUserOAuthed={useIsUserOAuthed}
														thirdPartyConfigs={thirdPartyConfigs}
														selectedDatasource={selectedDatasource}
													/>
												))}
											{/* Then render connected apps */}
											<ProductOptions
												options={groupedAppsBySection.connectedApps ?? []}
												thirdPartyConfigs={thirdPartyConfigs}
												selectedProduct={selectedProduct}
												selectedDatasource={selectedDatasource}
												onSelectProduct={onSelectProduct}
												useOAuthHandlers={useOAuthHandlers}
												useIsUserOAuthed={useIsUserOAuthed}
												allApps={allApps}
												primaryProduct={primaryProduct}
												anyConnectedConfluenceDC={anyConnectedConfluenceDC}
												anyConnectedJiraDC={anyConnectedJiraDC}
												anyConnectedJsmDC={anyConnectedJsmDC}
												resultCountsLoading={resultCountsLoading}
											/>
											{/* isConnected reflects the section context (connected apps section), not individual app status */}
											<ShowMorePopupWrapper
												options={groupedAppsBySection.connectedHiddenApps}
												useIsUserOAuthed={useIsUserOAuthed}
												useOAuthHandlers={useOAuthHandlers}
												thirdPartyConfigs={thirdPartyConfigs}
												isConnected={true}
												cloudId={cloudId}
												primaryProduct={primaryProduct}
											/>
										</Box>,
									]
								: []),
							// Then render unconnected apps section
							...((groupedAppsBySection.unconnectedApps.length ?? 0) > 0
								? [
										<Box
											xcss={unconnectedAppsContainerStylesSingleSelect}
											key="unconnected-section"
										>
											<SectionHeader
												key="unconnected-header"
												styleOverrides={unconnectedAppsHeaderStyleOverrides}
											>
												{formatMessage(messages.connectAppsHeader)}
											</SectionHeader>
											<Box xcss={unconnectedAppsDescriptionStyles}>
												<Text color="color.text.subtlest" size="medium" weight="regular">
													{formatMessage(messages.connectAppsDescription)}
												</Text>
											</Box>
											<ProductOptions
												options={groupedAppsBySection.unconnectedApps ?? []}
												thirdPartyConfigs={thirdPartyConfigs}
												selectedProduct={selectedProduct}
												selectedDatasource={selectedDatasource}
												onSelectProduct={onSelectProduct}
												useOAuthHandlers={useOAuthHandlers}
												useIsUserOAuthed={useIsUserOAuthed}
												allApps={allApps}
												primaryProduct={primaryProduct}
												anyConnectedConfluenceDC={anyConnectedConfluenceDC}
												anyConnectedJiraDC={anyConnectedJiraDC}
												anyConnectedJsmDC={anyConnectedJsmDC}
												resultCountsLoading={resultCountsLoading}
											/>
											{/* isConnected reflects the section context (unconnected apps section), not individual app status */}
											<ShowMorePopupWrapper
												options={groupedAppsBySection.unconnectedHiddenApps}
												useIsUserOAuthed={useIsUserOAuthed}
												useOAuthHandlers={useOAuthHandlers}
												thirdPartyConfigs={thirdPartyConfigs}
												isConnected={false}
												cloudId={cloudId}
												primaryProduct={primaryProduct}
											/>
										</Box>,
									]
								: []),
						]
					) : (
						// Original UI without section headers
						<>
							<ProductOptions
								options={allApps.visibleApps}
								thirdPartyConfigs={thirdPartyConfigs}
								selectedProduct={selectedProduct}
								selectedDatasource={selectedDatasource}
								onSelectProduct={onSelectProduct}
								useOAuthHandlers={useOAuthHandlers}
								useIsUserOAuthed={useIsUserOAuthed}
								allApps={allApps}
								primaryProduct={primaryProduct}
								anyConnectedConfluenceDC={anyConnectedConfluenceDC}
								anyConnectedJiraDC={anyConnectedJiraDC}
								anyConnectedJsmDC={anyConnectedJsmDC}
								resultCountsLoading={resultCountsLoading}
							/>
							{getUnifiedSearchRightRailEnhancementsExpValue() !== 'single-select' && (
								<ShowMorePopupWrapper
									options={allApps.hiddenApps}
									useIsUserOAuthed={useIsUserOAuthed}
									useOAuthHandlers={useOAuthHandlers}
									thirdPartyConfigs={thirdPartyConfigs}
									cloudId={cloudId}
									primaryProduct={primaryProduct}
								/>
							)}
						</>
					)}
				</Box>
			</CustomSpotlightTarget>
		</UFOSegment>
	);
};

export const getAppVisibility: (
	option: FilterOption,
	sidebarStoredState: SidebarStoredState | undefined,
	visibleApps: FilterOption[],
	onFilterHide: (productKey: ProductKeys) => void,
	selectedProduct?: ProductKeys,
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse | string,
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
) => 'visible' | 'hidden' = (
	option: FilterOption,
	sidebarStoredState: SidebarStoredState | undefined,
	visibleApps: FilterOption[],
	onFilterHide: (productKey: ProductKeys) => void,
	selectedProduct?: ProductKeys,
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse | string,
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
): 'visible' | 'hidden' => {
	// Always show "All" option or already selected options
	if (option.label === 'All' || option.isSelected) {
		return 'visible';
	}

	// Check if this product matches the selected product
	if (option.value === selectedProduct) {
		const hasFanout = thirdPartyConfigs?.[option.value as ProductKeys3P]?.fanOutConnectorSources;

		if (hasFanout) {
			// For fanout products, keep the base product visible to maintain ordering
			// The fanout rendering logic will handle showing the selected datasource
			return 'visible';
		} else {
			// For regular products, only show if no datasource is selected
			return !selectedDatasource ? 'visible' : 'hidden';
		}
	}

	// Always show primary product in the non-dynamic version of right-rail
	if (option.isPrimaryProduct) {
		return 'visible';
	}

	// Always show products from VISIBLE_PRIMARY_PRODUCTS section
	if (option.section === Sections.VISIBLE_PRIMARY_PRODUCTS) {
		return 'visible';
	}

	// Check if the user has manually hidden this option (must be checked before other section visibility)
	if (option.value && sidebarStoredState?.filters?.[option.value]?.hidden) {
		return 'hidden';
	}

	// Logic for the new dynamic sections
	if (
		option.section === Sections.VISIBLE_PRODUCTS_WITH_RESULTS ||
		option.section === Sections.VISIBLE_PRODUCTS_TO_AUTH
	) {
		return 'visible';
	}

	if (option.section === Sections.NESTED_PRODUCTS) {
		// Make it visible since it will be rendered within its category.
		return 'visible';
	}

	if (option.section === Sections.HIDDEN_PRODUCTS) {
		return 'hidden';
	}

	if (option.value && visibleApps.length > getMaxVisibleAppFilters()) {
		return 'hidden';
	} else {
		return 'visible';
	}
};
